import { IconNames } from '@blueprintjs/icons';
import { conditionalString, formatNameLastFirst } from '@strata/helper-functions';
import { ActionButtonText } from '@strata/react-buttons';
import { Dialog, InfoDialog } from '@strata/react-dialog';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { InlineMessage } from '@strata/react-inline-message';
import { HeaderLabelValuePair } from '@strata/react-label-value-pairs';
import { SuccessToast } from '@strata/react-toast-message';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { ClaimCodesContainer } from 'components/claims/ClaimCodesContainer';
import { ClaimTotalsGridAndCodes } from 'components/claims/ClaimTotalsGridAndCodes';
import { usePermissionCheck } from 'services/APIHooks';
import { Permissions } from 'services/constants';
import { handleError } from 'services/Error';
import {
    type ClaimFrequencyType,
    ClaimFrequencyTypes,
    ClaimSubmissionTypes,
} from 'services/MedicalBillingConstants';
import { AppRoutes } from 'services/routes';
import { useAppDispatch } from 'store';
import { type ClaimDetails } from 'store/api/medbillingApi';
import { type ReferralDetails } from 'store/api/treatmentApi';
import { submitClaim } from 'store/claims/claimDetailsThunks';

const DIALOG_TITLE = 'Claim Estimate';
const DIALOG_WIDTH = 1600;

const getPrimaryButtonLabel = (isNewEstimate: boolean, claimFrequencyType?: ClaimFrequencyType) =>
    isNewEstimate
        ? `Submit ${conditionalString(
              claimFrequencyType === ClaimFrequencyTypes.RESUBMIT,
              'Revised ',
          )}Claim`
        : 'Submit New Estimate/Claim';

const getSecondaryButtonLabel = (isNewEstimate: boolean) =>
    isNewEstimate ? 'Return To Referral' : 'Close';

interface ClaimEstimateDialogProps {
    referralId: string;
    referralDetails?: ReferralDetails;
    estimateData?: ClaimDetails;
    isNewEstimate?: boolean;
    previousEstimate?: ClaimDetails;
    isOpen: boolean;
    onClose: () => void;
}

export const ClaimEstimateDialog = ({
    referralId,
    referralDetails = undefined,
    estimateData = undefined,
    isNewEstimate = false,
    previousEstimate = undefined,
    isOpen,
    onClose,
    // eslint-disable-next-line complexity
}: ClaimEstimateDialogProps) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const writeMedBilling = usePermissionCheck(Permissions.WRITE_MED_BILLING);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const navigateToReferralDetails = () => {
        navigate({
            pathname: AppRoutes.wonReferralDetails(referralId),
            hash: 'claims-and-estimates',
        });
    };

    const hasIssues = () =>
        (estimateData?.claimIssues ?? []).length > 0 ||
        Object.keys(estimateData?.serviceIssues ?? {}).length > 0 ||
        (estimateData?.claimIssues ?? []).length > 0 ||
        (estimateData?.claimIssues ?? []).length > 0;

    return (
        <>
            <FullscreenLoading
                isShowing={isSubmitting}
                loadingText={
                    <>
                        Submitting Claim.
                        <br />
                        We appreciate your patience, this process could take up to one minute.
                    </>
                }
            />
            {!hasIssues() ? (
                <Dialog
                    dialogTitle={DIALOG_TITLE}
                    width={DIALOG_WIDTH}
                    isOpen={isOpen}
                    primaryButtonLabel={getPrimaryButtonLabel(
                        isNewEstimate,
                        estimateData?.claimFrequencyType,
                    )}
                    isPrimaryButtonDisabled={!writeMedBilling}
                    onPrimaryButtonClick={() => {
                        void (async () => {
                            if (isNewEstimate && estimateData && referralDetails) {
                                try {
                                    setIsSubmitting(true);

                                    const formattedClaimEstimate = {
                                        ...estimateData,
                                        submissionType: ClaimSubmissionTypes.ADJUDICATE,
                                        friendlyId: referralDetails.friendlyId,
                                        payerId: referralDetails.payerId,
                                    };

                                    await dispatch(submitClaim(formattedClaimEstimate));
                                    SuccessToast({ message: 'Claim submitted successfully.' });
                                    navigateToReferralDetails();
                                } catch (err) {
                                    setIsSubmitting(false);
                                    handleError(
                                        'An error occurred submitting claim. Please try again.',
                                        err as Error,
                                    );
                                }
                            } else {
                                navigate(AppRoutes.claimSubmissionForm(referralId), {
                                    state: {
                                        previousEstimate: estimateData,
                                    },
                                });
                            }
                        })();
                    }}
                    secondaryButtonLabel={getSecondaryButtonLabel(isNewEstimate)}
                    onSecondaryButtonClick={isNewEstimate ? navigateToReferralDetails : onClose}
                    isCloseButtonShown={!isNewEstimate}
                    dialogContent={
                        <>
                            <div className="padding__bottom--large">
                                <InlineMessage
                                    intent={InlineMessage.Intent.INFO}
                                    label="This estimate is not a guarantee of payment, benefits will be decided at the time of adjudicating a claim."
                                />
                            </div>
                            {previousEstimate && (
                                <div className="padding__bottom--large">
                                    <InlineMessage
                                        intent={InlineMessage.Intent.WARNING}
                                        label="Values highlighted in orange indicate a difference from the previous estimate."
                                    />
                                </div>
                            )}
                            <div className="flex__gap--x-large padding__bottom--large">
                                <HeaderLabelValuePair
                                    label="Patient"
                                    value={formatNameLastFirst(
                                        referralDetails?.memberLastName,
                                        referralDetails?.memberFirstName,
                                    )}
                                />
                                <HeaderLabelValuePair
                                    label="Claim Number"
                                    value={estimateData?.transactionId}
                                />
                                <div style={{ marginLeft: 'auto' }}>
                                    <ActionButtonText
                                        leftIcon={IconNames.PRINT}
                                        label="Print"
                                        id="print-claim-estimate-button"
                                        onClick={() => window.print()}
                                    />
                                </div>
                            </div>
                            <ClaimTotalsGridAndCodes
                                claimData={estimateData}
                                previousEstimate={previousEstimate}
                            />
                        </>
                    }
                />
            ) : (
                <InfoDialog
                    dialogTitle={DIALOG_TITLE}
                    width={DIALOG_WIDTH}
                    isOpen={isOpen}
                    headerIcon={IconNames.ERROR}
                    onClose={onClose}
                    dialogContent={
                        <>
                            <div className="padding__bottom--large">
                                <InlineMessage
                                    intent={InlineMessage.Intent.ERROR}
                                    label={`There was a problem submitting your estimate/claim, see the codes below. Error type ${
                                        (estimateData?.claimIssues ?? []).length > 0 ||
                                        Object.keys(estimateData?.serviceIssues ?? {}).length > 0
                                            ? '277'
                                            : '999'
                                    }.`}
                                />
                            </div>
                            <ClaimCodesContainer
                                categories={[
                                    {
                                        label: 'Claim Issues',
                                        codes: estimateData?.claimIssues,
                                    },
                                    ...Object.keys(estimateData?.serviceIssues ?? {}).map(
                                        (service) => ({
                                            label: `Service ${
                                                estimateData?.services?.findIndex(
                                                    (s) => s.procedureCode === service,
                                                ) ?? -1 + 1
                                            } Issues (${
                                                estimateData?.services?.find(
                                                    (s) => s.procedureCode === service,
                                                )?.procedureCodeDisplayText
                                            })`,
                                            codes: estimateData?.serviceIssues[service],
                                        }),
                                    ),
                                    {
                                        label: 'Data Segment Issues',
                                        codes: estimateData?.dataSegmentIssues,
                                    },
                                    {
                                        label: 'Data Element Issues',
                                        codes: estimateData?.dataElementIssues,
                                    },
                                ]}
                            />
                        </>
                    }
                />
            )}
        </>
    );
};
